/**
 * Item-level constraints for primitive-typed arrays.
 *
 * `extractSchemaFields` stashes everything under `items` except `type` in an
 * opaque `itemsRawMeta` bag so the subtree survives a round-trip. Nothing read
 * that bag, so constraints which genuinely reject data — the 30-code
 * `items.enum` on `manpower_utilization.person_employer_company_trade`, for
 * instance — were enforced by the producer but invisible in the builder.
 *
 * Shared by the read-only field panel and the field modal so the two cannot
 * describe the same field differently.
 */

// Object- and array-typed items also populate `itemsRawMeta`, but with
// structural keywords these scalar rows would misrepresent.
const PRIMITIVE_ITEM_TYPES = new Set(['string', 'integer', 'number', 'boolean']);

/**
 * Whether an array's item type carries editable scalar keywords. Object- and
 * array-typed items are structural and are edited through their children.
 *
 * @param {string} itemType resolved `items.type`
 * @returns {boolean}
 */
export function isPrimitiveArrayItemType(itemType) {
	return PRIMITIVE_ITEM_TYPES.has(itemType);
}

const FORMAT_LABELS = {
	email: 'email',
	uri: 'URI',
	uuid: 'UUID',
	date: 'date',
	'date-time': 'date-time',
};

// Row order, mirroring the field-level constraint ordering in the panel.
const ITEM_ROWS = [
	['minLength', 'Item min length'],
	['maxLength', 'Item max length'],
	['minimum', 'Item minimum'],
	['maximum', 'Item maximum'],
	['pattern', 'Item pattern'],
];

function isMeaningful(value) {
	if (value == null || value === '') return false;
	if (Array.isArray(value)) return value.length > 0;
	if (typeof value === 'string') return value.trim() !== '';
	return true;
}

// A `null` member of an item enum is authored intent even when it is
// unreachable (a bare `items.type: string` rejects it). Show it rather than
// letting Array.join render it as an empty string.
function joinItemEnum(values) {
	return values.map(member => (member === null ? 'null' : member)).join(', ');
}

/**
 * The item-keyword bag for a primitive-typed array, or null when the field is
 * not one. Single gate shared by every export here so the read-only rows, the
 * form projection and the merge can never disagree about what qualifies.
 */
function fieldItemMeta(field) {
	if (field?.dataType !== 'array') return null;
	if (!PRIMITIVE_ITEM_TYPES.has(field.arrayItemType)) return null;
	const items = field.itemsRawMeta;
	return items && typeof items === 'object' ? items : null;
}

// Keywords the field modal owns. Anything else under `items` (format,
// contains, $comment, ...) is preserved untouched on merge — the editor must
// never silently drop what it cannot author.
const EDITABLE_ITEM_KEYWORDS = ['enum', 'minLength', 'maxLength', 'pattern'];

const NUMERIC_ITEM_TYPES = new Set(['integer', 'number']);

/**
 * Coerce a comma-separated enum string to the array item's type.
 *
 * The scalar equivalent (`coerceEnumValues` in dsv-schema-utils) keys off
 * `field.dataType`, which for an array field is literally `'array'` — so it can
 * never type an item enum correctly. This keys off `arrayItemType` instead.
 *
 * A `null` member is NOT parsed from the text: it cannot survive a join/split
 * round-trip, so it is carried by an explicit flag, mirroring the scalar
 * "Accept null" checkbox.
 *
 * @param {string} text comma-separated values
 * @param {string} itemType resolved `items.type`
 * @returns {Array|null} coerced members, or null when the list is empty
 */
export function coerceItemEnumValues(text, itemType) {
	const members = String(text ?? '')
		.split(',')
		.map(part => part.trim())
		.filter(part => part !== '');
	if (members.length === 0) return null;

	if (NUMERIC_ITEM_TYPES.has(itemType)) {
		return members.map(member => {
			const parsed = itemType === 'integer' ? Number.parseInt(member, 10) : Number.parseFloat(member);
			return Number.isNaN(parsed) ? member : parsed;
		});
	}
	if (itemType === 'boolean') {
		return members.map(member => {
			if (member === 'true') return true;
			if (member === 'false') return false;
			return member;
		});
	}
	return members;
}

/**
 * Project a field's item keywords onto the modal's flat form state.
 *
 * @param {object} field field-model entry
 * @returns {{itemsEnumValues: string, itemsAcceptNull: boolean, itemsMinLength: string,
 *   itemsMaxLength: string, itemsPattern: string}}
 */
export function splitItemConstraintsForForm(field) {
	const items = fieldItemMeta(field) || {};
	const members = Array.isArray(items.enum) ? items.enum : [];
	const asText = value => (value == null ? '' : String(value));

	return {
		// `null` is stripped out of the text and carried by the checkbox.
		itemsEnumValues: members.filter(member => member !== null).join(', '),
		itemsAcceptNull: members.includes(null),
		itemsMinLength: asText(items.minLength),
		itemsMaxLength: asText(items.maxLength),
		itemsPattern: asText(items.pattern),
	};
}

/**
 * Fold the modal's item fields back into an `itemsRawMeta` bag.
 *
 * Cleared inputs DELETE their keyword rather than leaving the previous value in
 * place — without that the editor could add an item constraint but never remove
 * one. Keywords the modal does not author are carried over verbatim.
 *
 * @param {object} formData modal form state
 * @param {string} itemType resolved `items.type`, used to coerce the enum
 * @param {object} [existing] the field's current itemsRawMeta
 * @returns {object|undefined} the new bag; `{}` when the field HAD keywords and
 *   the author cleared them all, `undefined` when it never had any. The emitter
 *   treats a present bag as authoritative, so collapsing the emptied case to
 *   `undefined` would fall back to the persisted `items` and resurrect exactly
 *   what was just deleted.
 */
export function mergeItemConstraintsIntoRawMeta(formData, itemType, existing) {
	const next = { ...(existing || {}) };
	for (const keyword of EDITABLE_ITEM_KEYWORDS) delete next[keyword];

	const members = coerceItemEnumValues(formData.itemsEnumValues, itemType);
	if (members) {
		next.enum = formData.itemsAcceptNull ? [...members, null] : members;
	} else if (formData.itemsAcceptNull) {
		// "Accept null" with no other members is a null-only enum — degenerate,
		// so treat it as no enum at all rather than emitting `enum: [null]`.
		delete next.enum;
	}

	const asInt = value => {
		if (value == null || String(value).trim() === '') return null;
		const parsed = Number.parseInt(value, 10);
		return Number.isNaN(parsed) ? null : parsed;
	};
	const minLength = asInt(formData.itemsMinLength);
	if (minLength != null) next.minLength = minLength;
	const maxLength = asInt(formData.itemsMaxLength);
	if (maxLength != null) next.maxLength = maxLength;
	const pattern = String(formData.itemsPattern ?? '').trim();
	if (pattern !== '') next.pattern = pattern;

	if (Object.keys(next).length > 0) return next;
	const hadKeywords = existing && Object.keys(existing).length > 0;
	return hadKeywords ? {} : undefined;
}

/**
 * @param {object} field field-model entry from extractSchemaFields
 * @returns {Array<[string, string|number]>} label/value rows, empty when the
 *   field is not a primitive array or carries no item keywords
 */
export function collectItemConstraintRows(field) {
	const items = fieldItemMeta(field);
	if (!items) return [];

	const rows = [];
	if (isMeaningful(items.enum)) rows.push(['Allowed item values', joinItemEnum(items.enum)]);
	for (const [keyword, label] of ITEM_ROWS) {
		if (isMeaningful(items[keyword])) rows.push([label, items[keyword]]);
	}
	if (isMeaningful(items.format)) {
		rows.push(['Item format', FORMAT_LABELS[items.format] ?? items.format]);
	}
	return rows;
}
